import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { LandSearchFilters } from '../../pages/Explore/DeveloperMap/Lands/types/landFilters.types'

interface LandsFilterState {
  filters: LandSearchFilters
  hasActiveFilters: boolean
  activeFilterCount: number
  filterMode: 'manual' | 'ai'
}

const initialState: LandsFilterState = {
  filters: {},
  hasActiveFilters: false,
  activeFilterCount: 0,
  filterMode: 'manual',
}

// Helper function to check if filters have active values
const calculateActiveFilters = (filters: LandSearchFilters) => {
  let count = 0

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'mode') return // Skip mode from count

    if (typeof value === 'object' && value !== null) {
      // Range objects
      if ('min' in value || 'max' in value) {
        if (value.min !== undefined || value.max !== undefined) count++
      }
    } else if (Array.isArray(value)) {
      // Array filters
      if (value.length > 0) count++
    } else if (typeof value === 'string') {
      // String filters
      if (value !== '') count++
    } else if (typeof value === 'boolean' || typeof value === 'number') {
      // Boolean/number filters
      count++
    }
  })

  return {
    hasActiveFilters: count > 0,
    activeFilterCount: count,
  }
}

const landsFilterSlice = createSlice({
  name: 'landsFilter',
  initialState,
  reducers: {
    updateFilter: <K extends keyof LandSearchFilters>(
      state: LandsFilterState,
      action: PayloadAction<{ key: K; value: LandSearchFilters[K] }>
    ) => {
      const { key, value } = action.payload
      state.filters[key] = value

      // Recalculate active filter status
      const activeStatus = calculateActiveFilters(state.filters)
      state.hasActiveFilters = activeStatus.hasActiveFilters
      state.activeFilterCount = activeStatus.activeFilterCount
    },

    updateFilters: (
      state,
      action: PayloadAction<Partial<LandSearchFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }

      // Recalculate active filter status
      const activeStatus = calculateActiveFilters(state.filters)
      state.hasActiveFilters = activeStatus.hasActiveFilters
      state.activeFilterCount = activeStatus.activeFilterCount
    },

    setFilters: (state, action: PayloadAction<LandSearchFilters>) => {
      console.log('landsFilterSlice - setFilters called with:', action.payload)
      state.filters = action.payload

      // Recalculate active filter status
      const activeStatus = calculateActiveFilters(state.filters)
      state.hasActiveFilters = activeStatus.hasActiveFilters
      state.activeFilterCount = activeStatus.activeFilterCount
    },

    clearFilters: (state) => {
      state.filters = {}
      state.hasActiveFilters = false
      state.activeFilterCount = 0
    },

    setFilterMode: (state, action: PayloadAction<'manual' | 'ai'>) => {
      state.filterMode = action.payload
    },

    setFiltersFromUrl: (state, action: PayloadAction<LandSearchFilters>) => {
      state.filters = action.payload

      // Recalculate active filter status
      const activeStatus = calculateActiveFilters(state.filters)
      state.hasActiveFilters = activeStatus.hasActiveFilters
      state.activeFilterCount = activeStatus.activeFilterCount
    },
  },
})

export const {
  updateFilter: updateLandFilter,
  updateFilters: updateLandFilters,
  setFilters: setLandFilters,
  clearFilters: clearLandFilters,
  setFilterMode: setLandFilterMode,
  setFiltersFromUrl: setLandFiltersFromUrl,
} = landsFilterSlice.actions

// Selectors
export const selectLandFilters = (state: { landsFilter: LandsFilterState }) =>
  state.landsFilter.filters

export const selectLandFiltersActiveStatus = (state: {
  landsFilter: LandsFilterState
}) => ({
  hasActiveFilters: state.landsFilter.hasActiveFilters,
  activeFilterCount: state.landsFilter.activeFilterCount,
})

export const selectLandFilterMode = (state: {
  landsFilter: LandsFilterState
}) => state.landsFilter.filterMode

// Selector for specific filter values
export const selectLandFilterValue =
  <K extends keyof LandSearchFilters>(key: K) =>
  (state: { landsFilter: LandsFilterState }) =>
    state.landsFilter.filters[key]

export const landsFilterReducer = { landsFilter: landsFilterSlice.reducer }
